import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography, IconButton, Tooltip, Select, MenuItem,
  FormControl, InputLabel, TextField, Button, Divider,
  CircularProgress, Accordion, AccordionSummary, AccordionDetails, Chip,
  ToggleButton, ToggleButtonGroup,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FavoriteIcon from '@mui/icons-material/Favorite';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import ViewListIcon from '@mui/icons-material/ViewList';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import HealthChart from '../components/health/HealthChart';
import DailyHealthSummaryTable from '../components/health/DailyHealthSummaryTable';
import { useTranslation } from 'react-i18next';
import { useMessage } from '../contexts/MessageContext';
import {
  getCachedLifeData, deleteHealthRecord, loadLifeData,
  type HealthRecord, type HealthTypeConfig,
} from '../services/lifeDataService';
import HealthRecordDialog from '../components/health/HealthRecordDialog';
import '../styles/DataTable.css';

/* ── value display helpers ─────────────────────────────────────────────────── */
function formatValues(record: HealthRecord, config: HealthTypeConfig | undefined): string {
  if (!config || config.valueSchema.length === 0) return '';
  if (config.name === '血壓' || (config.valueSchema.length >= 3)) {
    const [s, d, h] = record.values;
    return `${s ?? '?'}/${d ?? '?'} HR${h ?? '?'}`;
  }
  return record.values.map((v, i) => {
    const schema = config.valueSchema[i];
    return schema ? `${v} ${schema.unit}` : String(v);
  }).join(' / ');
}

function healthColor(record: HealthRecord): string {
  if (record.type === '血糖' && record.values[0] != null) {
    const v = record.values[0];
    if (v > 200) return '#ef5350';
    if (v >= 126) return '#ffa726';
    return '#66bb6a';
  }
  if (record.type === '血壓' && record.values[0] != null) {
    return record.values[0] > 140 ? '#ef5350' : '#66bb6a';
  }
  return '#42a5f5';
}

/* ── group records by date ─────────────────────────────────────────────────── */
function groupByDate(records: HealthRecord[]): Map<string, HealthRecord[]> {
  const map = new Map<string, HealthRecord[]>();
  const sorted = [...records].sort((a, b) => b.ts.localeCompare(a.ts));
  for (const r of sorted) {
    const date = r.ts.slice(0, 10);
    if (!map.has(date)) map.set(date, []);
    map.get(date)!.push(r);
  }
  return map;
}

function formatDateHeader(dateStr: string, isZh: boolean): string {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString(isZh ? 'zh-TW' : 'en-US', {
    year: 'numeric', month: 'long', day: 'numeric', weekday: 'short',
  });
}

function formatTime(ts: string): string {
  return ts.slice(11, 16); // HH:MM from "2026-05-19T05:54"
}

/* 當日各指標摘要："血糖×3 · 血壓×1" */
function daySummary(records: HealthRecord[]): string {
  const counts = new Map<string, number>();
  for (const r of records) counts.set(r.type, (counts.get(r.type) ?? 0) + 1);
  return Array.from(counts.entries()).map(([type, n]) => `${type}×${n}`).join(' · ');
}

const DAYS_PER_PAGE = 10;

/* ── HealthPage ────────────────────────────────────────────────────────────── */
const HealthPage: React.FC = () => {
  const { i18n, t } = useTranslation();
  const isZh = !i18n.language.startsWith('en');
  const { showSuccess, showApiError } = useMessage();

  const [health, setHealth]             = useState(() => getCachedLifeData().health);
  const [loading, setLoading]           = useState(false);
  const [filterType, setFilterType]     = useState('');
  const [filterFrom, setFilterFrom]     = useState('');
  const [filterTo, setFilterTo]         = useState('');
  const [showFilters, setShowFilters]   = useState(false);
  const [viewMode, setViewMode]         = useState<'list' | 'chart'>('list');

  // Dialog state
  const [dlgOpen, setDlgOpen]           = useState(false);
  const [editRecord, setEditRecord]     = useState<HealthRecord | undefined>(undefined);
  const [deleteId, setDeleteId]         = useState<string | null>(null);

  // Reload from cache after login/API refresh
  useEffect(() => {
    const cached = getCachedLifeData().health;
    if (cached.records.length > 0 || cached.config.types.length > 0) {
      setHealth(cached);
    } else {
      setLoading(true);
      loadLifeData()
        .then(d => setHealth(d.health))
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, []);

  const refresh = useCallback(() => {
    setHealth(getCachedLifeData().health);
  }, []);

  const handleDelete = useCallback(async (id: string) => {
    try {
      await deleteHealthRecord(id);
      showSuccess('SYS310023');
      setDeleteId(null);
      refresh();
    } catch (err) {
      showApiError(err);
    }
  }, [showSuccess, showApiError, refresh, t]);

  const handleEdit = (record: HealthRecord) => {
    setEditRecord(record);
    setDlgOpen(true);
  };

  const handleAdd = () => {
    setEditRecord(undefined);
    setDlgOpen(true);
  };

  // Filter records
  const filteredRecords = health.records.filter(r => {
    if (filterType && r.type !== filterType) return false;
    const date = r.ts.slice(0, 10);
    if (filterFrom && date < filterFrom) return false;
    if (filterTo && date > filterTo) return false;
    return true;
  });

  const groups = groupByDate(filteredRecords);
  const dayEntries = Array.from(groups.entries());   // [date, records][]，已依日期 DESC

  // 預設全部展開（取 date key 的 Set）
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set<string>());

  // 分頁：一列 = 一天，預設 10 天/頁
  const [dayPage, setDayPage] = useState(1);
  const totalDayPages = Math.max(1, Math.ceil(dayEntries.length / DAYS_PER_PAGE));
  const pageStart = (dayPage - 1) * DAYS_PER_PAGE;
  const pagedDayEntries = dayEntries.slice(pageStart, pageStart + DAYS_PER_PAGE);

  // 當 groups 變動時（初次載入 or 篩選），只展開最新日期 + 回第 1 頁
  useEffect(() => {
    const dates = Array.from(groups.keys()).sort().reverse();
    const latest = dates[0];
    setExpanded(latest ? new Set([latest]) : new Set());
    setDayPage(1);
  }, [filteredRecords.length, filterType, filterFrom, filterTo]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleDate = (date: string) => {
    setExpanded(prev => {
      const next = new Set<string>(Array.from(prev));
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  };

  const typeOptions = health.config.types.map(t => t.name);

  return (
    <div style={{ padding: '28px 40px', minHeight: '100vh', background: '#f5f7fa' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <div style={{
          width: 40, height: 40, borderRadius: '50%',
          background: 'linear-gradient(135deg, #e91e63 0%, #f06292 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <FavoriteIcon sx={{ color: '#fff', fontSize: 22 }} />
        </div>
        <div>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a237e', lineHeight: 1 }}>
            {isZh ? '健康紀錄' : 'Health Records'}
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#9e9e9e', mt: 0.3 }}>
            {isZh
              ? `共 ${health.records.length} 筆紀錄`
              : `${health.records.length} records`}
          </Typography>
        </div>

        <div style={{ flex: 1 }} />

        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, v) => v && setViewMode(v)}
          size="small"
          sx={{ mr: 1 }}
        >
          <ToggleButton value="list" sx={{ px: 1.5 }}>
            <Tooltip title={isZh ? '清單' : 'List'}>
              <ViewListIcon fontSize="small" />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="chart" sx={{ px: 1.5 }}>
            <Tooltip title={isZh ? '圖表' : 'Chart'}>
              <ShowChartIcon fontSize="small" />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>

        <Tooltip title={isZh ? '篩選' : 'Filter'}>
          <IconButton
            size="small"
            onClick={() => setShowFilters(p => !p)}
            sx={{
              background: showFilters ? '#e3f2fd' : undefined,
              color: showFilters ? '#1976d2' : '#757575',
            }}
          >
            <FilterListIcon />
          </IconButton>
        </Tooltip>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          sx={{
            background: 'linear-gradient(135deg, #e91e63 0%, #f06292 100%)',
            '&:hover': { background: 'linear-gradient(135deg, #c2185b 0%, #e91e63 100%)' },
            borderRadius: 2, fontWeight: 600,
          }}
        >
          {isZh ? '手動新增' : 'Add Record'}
        </Button>
      </div>

      {/* Filter bar */}
      {showFilters && (
        <div style={{
          display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20,
          background: '#fff', borderRadius: 12,
          padding: '14px 18px',
          boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
        }}>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>{isZh ? '類型' : 'Type'}</InputLabel>
            <Select
              value={filterType}
              label={isZh ? '類型' : 'Type'}
              onChange={e => setFilterType(e.target.value)}
            >
              <MenuItem value="">{isZh ? '全部' : 'All'}</MenuItem>
              {typeOptions.map(t => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            type="date" size="small"
            label={isZh ? '開始日期' : 'From'}
            value={filterFrom}
            onChange={e => setFilterFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 155 }}
          />
          <TextField
            type="date" size="small"
            label={isZh ? '結束日期' : 'To'}
            value={filterTo}
            onChange={e => setFilterTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 155 }}
          />
          <Button
            size="small"
            onClick={() => { setFilterType(''); setFilterFrom(''); setFilterTo(''); }}
            sx={{ color: '#9e9e9e', fontSize: 12 }}
          >
            {isZh ? '清除篩選' : 'Clear'}
          </Button>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
          <CircularProgress sx={{ color: '#e91e63' }} />
        </div>
      ) : viewMode === 'chart' ? (
        <>
          <HealthChart records={filteredRecords} isZh={isZh} />
          <DailyHealthSummaryTable
            records={filteredRecords}
            dateRange={{ from: filterFrom || undefined, to: filterTo || undefined }}
          />
        </>
      ) : filteredRecords.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '80px 0', color: '#9e9e9e' }}>
          <FavoriteIcon sx={{ fontSize: 64, color: '#f8bbd0', mb: 2 }} />
          <Typography sx={{ fontSize: 16, color: '#bdbdbd' }}>
            {isZh ? '尚無健康紀錄' : 'No health records yet'}
          </Typography>
          <Typography sx={{ fontSize: 13, mt: 1, color: '#e0e0e0' }}>
            {isZh
              ? '在世界天氣地球上點選「🩺 健康」後點選地點，即可新增紀錄'
              : 'Enable Health on the globe and click a location to add a record'}
          </Typography>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {pagedDayEntries.map(([date, records]) => (
            <Accordion
              key={date}
              expanded={expanded.has(date)}
              onChange={() => toggleDate(date)}
              disableGutters
              sx={{
                borderRadius: '12px !important',
                boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
                '&:before': { display: 'none' },
                overflow: 'hidden',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: '#e91e63' }} />}
                sx={{
                  background: 'linear-gradient(90deg, #fce4ec 0%, #f5f7fa 100%)',
                  minHeight: 48,
                  px: 2.5,
                  '&.Mui-expanded': { minHeight: 48 },
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1565c0' }}>
                    {formatDateHeader(date, isZh)}
                  </Typography>
                  <Chip
                    label={records.length}
                    size="small"
                    sx={{
                      background: '#e91e63',
                      color: '#fff',
                      fontWeight: 700,
                      height: 20,
                      fontSize: 12,
                      '.MuiChip-label': { px: 1 },
                    }}
                  />
                  {/* 當日各指標摘要 */}
                  <Typography sx={{ fontSize: 11, color: '#78909c' }}>
                    {daySummary(records)}
                  </Typography>
                </div>
              </AccordionSummary>

              <AccordionDetails sx={{ background: '#fafbfc', p: '12px 16px 16px' }}>
              {/* Records for this date */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {records.map(record => {
                  const typeConfig = health.config.types.find(t => t.name === record.type);
                  const color = healthColor(record);
                  const valStr = formatValues(record, typeConfig);

                  return (
                    <div
                      key={record.id}
                      style={{
                        background: '#fff',
                        borderRadius: 12,
                        boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
                        display: 'flex',
                        alignItems: 'stretch',
                        overflow: 'hidden',
                        border: '1px solid #f5f5f5',
                      }}
                    >
                      {/* Color bar */}
                      <div style={{ width: 4, background: color, flexShrink: 0 }} />

                      {/* Main content */}
                      <div style={{ flex: 1, padding: '12px 16px', display: 'flex', gap: 16, alignItems: 'center' }}>
                        {/* Time */}
                        <div style={{ width: 48, flexShrink: 0, textAlign: 'center' }}>
                          <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#37474f', lineHeight: 1 }}>
                            {formatTime(record.ts)}
                          </Typography>
                          <Typography sx={{ fontSize: 10, color: '#9e9e9e', mt: 0.3 }}>
                            {record.timezone?.replace('Asia/', '') ?? ''}
                          </Typography>
                        </div>

                        <Divider orientation="vertical" flexItem />

                        {/* Type + condition */}
                        <div style={{ minWidth: 100 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1a237e' }}>
                            {record.type}
                          </Typography>
                          {record.condition && (
                            <Typography sx={{ fontSize: 11, color: '#757575' }}>
                              {record.condition}
                            </Typography>
                          )}
                        </div>

                        {/* Values */}
                        {valStr && (
                          <div style={{
                            background: `${color}18`,
                            border: `1px solid ${color}44`,
                            borderRadius: 8,
                            padding: '4px 12px',
                            minWidth: 80, textAlign: 'center',
                          }}>
                            <Typography sx={{ fontSize: 16, fontWeight: 700, color, lineHeight: 1.2 }}>
                              {valStr}
                            </Typography>
                          </div>
                        )}

                        {/* Food / medicine */}
                        {(record.food || record.medicine) && (
                          <Typography sx={{ fontSize: 12, color: '#607d8b', flex: 1 }}>
                            {[record.food, record.medicine].filter(Boolean).join(' · ')}
                          </Typography>
                        )}

                        {/* Notes */}
                        {record.notes && (
                          <Typography sx={{ fontSize: 12, color: '#90a4ae', flex: 1 }}>
                            {record.notes}
                          </Typography>
                        )}

                        {/* Location */}
                        {record.location && (
                          <Typography sx={{ fontSize: 11, color: '#bdbdbd', flexShrink: 0 }}>
                            📍 {record.location.placeName}
                          </Typography>
                        )}
                      </div>

                      {/* Actions */}
                      <div style={{ display: 'flex', alignItems: 'center', paddingRight: 8, gap: 0 }}>
                        <Tooltip title={isZh ? '編輯' : 'Edit'}>
                          <IconButton size="small" onClick={() => handleEdit(record)}
                            sx={{ color: '#bdbdbd', '&:hover': { color: '#1976d2' } }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={isZh ? '刪除' : 'Delete'}>
                          <IconButton size="small" onClick={() => setDeleteId(record.id)}
                            sx={{ color: '#bdbdbd', '&:hover': { color: '#e53935' } }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </div>
                  );
                })}
              </div>
              </AccordionDetails>
            </Accordion>
          ))}

          {/* 分頁控制（一列 = 一天，10 天/頁）*/}
          {totalDayPages > 1 && (
            <div className="pagination-container" style={{ marginTop: 8 }}>
              <div className="pagination-info">
                <span className="pagination-text">
                  {isZh
                    ? `共 ${dayEntries.length} 天，第 ${dayPage} / ${totalDayPages} 頁`
                    : `${dayEntries.length} days, page ${dayPage} / ${totalDayPages}`}
                </span>
              </div>
              <div className="pagination-buttons">
                <button className="btn-pagination" onClick={() => setDayPage(1)}
                        disabled={dayPage === 1}>⟪</button>
                <button className="btn-pagination" onClick={() => setDayPage(p => Math.max(1, p - 1))}
                        disabled={dayPage === 1}>‹</button>
                {Array.from({ length: totalDayPages }, (_, i) => i + 1)
                  .filter(page => {
                    if (totalDayPages <= 7) return true;
                    if (page === 1 || page === totalDayPages) return true;
                    if (page >= dayPage - 1 && page <= dayPage + 1) return true;
                    return false;
                  })
                  .map((page, index, array) => {
                    const node = (
                      <button
                        key={page}
                        className={`btn-pagination ${dayPage === page ? 'active' : ''}`}
                        onClick={() => setDayPage(page)}
                      >
                        {page}
                      </button>
                    );
                    if (index > 0 && array[index - 1] !== page - 1) {
                      return (
                        <React.Fragment key={`ellipsis-${page}`}>
                          <span className="pagination-ellipsis">...</span>
                          {node}
                        </React.Fragment>
                      );
                    }
                    return node;
                  })}
                <button className="btn-pagination" onClick={() => setDayPage(p => Math.min(totalDayPages, p + 1))}
                        disabled={dayPage === totalDayPages}>›</button>
                <button className="btn-pagination" onClick={() => setDayPage(totalDayPages)}
                        disabled={dayPage === totalDayPages}>⟫</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Glucose daily pivot table — shown below both list and chart views */}
      {!loading && filteredRecords.length > 0 && viewMode === 'list' && (
        <DailyHealthSummaryTable
          records={filteredRecords}
          dateRange={{ from: filterFrom || undefined, to: filterTo || undefined }}
        />
      )}

      {/* Delete confirm dialog（標準 MUI pattern，取代底端白條）*/}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {isZh ? '確認刪除' : 'Confirm Delete'}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>
            {isZh ? '確定要刪除這筆紀錄嗎？此操作無法復原。'
                  : 'Delete this record? This cannot be undone.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteId(null)} sx={{ color: '#757575' }}>
            {isZh ? '取消' : 'Cancel'}
          </Button>
          <Button variant="contained" color="error"
            onClick={() => deleteId && handleDelete(deleteId)}>
            {isZh ? '刪除' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add / Edit dialog */}
      <HealthRecordDialog
        open={dlgOpen}
        editRecord={editRecord}
        healthConfig={health.config}
        onClose={() => setDlgOpen(false)}
        onSaved={(isNew) => {
          // lint-msg-allow: ternary of valid 9-char codes (SYS310021/SYS310022), regex false positive
          showSuccess(isNew ? 'SYS310021' : 'SYS310022');
          refresh();
          setDlgOpen(false);
        }}
      />
    </div>
  );
};

export default HealthPage;
